import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId, type TestIdProps } from '../utils';
import { Icon } from './icons/Icon';
import type { IconName } from '../icons/names';

const emptyStateVariants = cva('flex flex-col items-center justify-center text-foreground-muted', {
  variants: {
    size: {
      sm: 'h-32 gap-sm',
      md: 'h-64 gap-md',
      lg: 'h-96 gap-md'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

type EmptyStateVariantProps = VariantProps<typeof emptyStateVariants>;

type BaseEmptyStateAttributes = Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

export interface EmptyStateProps
  extends BaseEmptyStateAttributes, EmptyStateVariantProps, TestIdProps {
  /** Icon to display (FontAwesome icon name) */
  icon: IconName;
  /** Primary message text */
  message: string;
  /** Optional secondary description */
  description?: string;
  /** Optional action element (e.g. a Button) */
  action?: React.ReactNode;
  id?: string;
}

/**
 * EmptyState component
 *
 * Displays a centered placeholder when a list or view has no content.
 *
 * @example
 * ```tsx
 * <EmptyState icon="file-invoice-dollar" message={t('empty')} />
 * ```
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon, message, description, action, size, className, testId, id, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-testid={getTestId({ testId, id })}
        className={cn(emptyStateVariants({ size }), className)}
        {...props}
      >
        <Icon name={icon} size='3xl' className='text-secondary-base' />
        <p className='text-lg font-medium'>{message}</p>
        {description && <p className='text-sm text-foreground-muted'>{description}</p>}
        {action && <div className='mt-sm'>{action}</div>}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';
